'use client';

import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap/all';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { Text, Container, useUi } from '@weareconceptstudio/core';

//* Helpers
import { config } from '@/helpers';

//* HOC's
import { withDataContext } from '@/context';

//* Components
import Button from '@/components/Button';
import TitleAnim from '@/modules/_animation/Title';
import LottieAnimation from '@/components/LottieAnimation';

//* Styles
import CallToActionStyle from './style';

//* Lottie File
import handOlive from '@/lottie/hand-olive.json';
import hand from './image-svg/home-hand2.svg';

gsap.registerPlugin(ScrollTrigger);

const CallToAction = ({ globalData }) => {
	const { call_to_action_text } = globalData;

	const { isMobile } = useUi();

	//! Ref
	const triggerRef = useRef();
	const sectionRef = useRef();
	const firstLottieRef = useRef();
	const secondLottieRef = useRef();
	const timeoutRef = useRef();
	const titleRef = useRef();
	const buttonRef = useRef();
	const leaveController = useRef(true);

	//! State
	const [play, setPlay] = useState(false);

	useEffect(() => {
		if (sectionRef.current && globalData) {
			triggerRef.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: sectionRef.current,
				onEnter: () => {
					if (leaveController.current) {
						leaveController.current = false;
						gsap.set(firstLottieRef.current, { rotate: 90, x: '-100%', transformOrigin: '0% 100%' });
						gsap.set(secondLottieRef.current, { rotate: -90, x: '200%', y: `${isMobile ? '0%' : '100%'}`, transformOrigin: '100% 100%' });
						gsap.set(buttonRef.current, { scale: 0 });

						timeoutRef.current = setTimeout(() => {
							setPlay(true);
							gsap.to(firstLottieRef.current, { rotate: 15, x: 0, ease: 'power2.out', duration: 1.2 });
							gsap.to(secondLottieRef.current, { x: 0, rotate: -6, y: 0, ease: 'power2.out', duration: 1.2 });
							gsap.to(buttonRef.current, { scale: 1, ease: 'power2.out', duration: 0.8, delay: 0.2 });
						}, 100);
					}
				},
				onLeaveBack: () => {
					leaveController.current = true;

					setPlay(false);
					gsap.killTweensOf([firstLottieRef.current, secondLottieRef.current, buttonRef.current]);
					gsap.set(firstLottieRef.current, { x: '-100%', rotate: 90 });
					gsap.set(secondLottieRef.current, { x: '200%', rotate: -90 });
					gsap.set(buttonRef.current, { scale: 0 });

					if (timeoutRef.current) {
						clearTimeout(timeoutRef.current);
					}
				},
			});
		}

		return () => {
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	});

	return (
		<CallToActionStyle
			ref={sectionRef}
			className='call-to-action-wrapper'>
			<Container
				row
				className={'call-to-action-container'}>
				<div className={'col-2 col-m-12'}>
					<div className='lottie-wrap lottie-wrap-1'>
						<div
							className='first-lottie-wrap'
							ref={firstLottieRef}>
							<div className={`lottie-first`}>
								<LottieAnimation
									key={play + 'd'}
									render={'svg'}
									autoplay={false}
									isPlay={play}
									isReverse={false}
									loop={false}
									animData={handOlive}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className={'col-8 col-m-12'}>
					<div className='call-to-text-wrapper'>
						<TitleAnim
							animRefTrigger={sectionRef}
							load={true}>
							<Text
								ref={titleRef}
								tag={'span'}
								className={'h3 font-larken-light white-1000 desc'}>
								{call_to_action_text}
							</Text>
						</TitleAnim>

						<div
							ref={buttonRef}
							className={'button-wrapper'}>
							<Button
								btnType={'yellow-type'}
								url={config.routes.contactUs.path}
								text={config.routes.contactUs.name}
							/>
						</div>
					</div>
				</div>

				<div className={'col-2 col-m-12'}>
					<div className='lottie-wrap lottie-wrap-2'>
						<div
							className='second-lottie-wrap'
							ref={secondLottieRef}>
							<div className={`lottie-second`}>
								<object
									id={'svg-image'}
									type='image/svg+xml'
									data={hand.src}
								/>
								{/* <LottieAnimation
									key={play + 's'}
									render={'svg'}
									autoplay={false}
									isPlay={play}
									isReverse={false}
									loop={false}
									animData={hand}
									duration={0.8}
								/> */}
							</div>
						</div>
					</div>
				</div>
			</Container>
		</CallToActionStyle>
	);
};

export default withDataContext(CallToAction, ['globalData']);
